"use client";

import { useEffect, useMemo, useState } from "react";
import type { VisitorStorage } from "@/lib/visitor-storage";

/** Last known contact-level block, per integration (the same scope as the stored visitor id). */
export const blockedKey = (publicKey: string | null): string =>
  `wexio:visitor-blocked:${publicKey ?? ""}`;

function readPersisted(publicKey: string | null, storage: VisitorStorage): boolean {
  try {
    return storage.getItem(blockedKey(publicKey)) === "1";
  } catch {
    return false;
  }
}

/**
 * The ONE "this visitor is blocked" signal (contact level). Sources, freshest first: the live
 * `blockScope: CONTACT` event, the threads page (`visitorBlocked`), the `/session` answer (`blocked`,
 * unknown on an old BE) and the handshake flag. Until one of them has answered, the last known value
 * from storage stands in, so a blocked visitor's launcher message never flashes on a reload.
 * `null` = that source has not answered (or does not exist).
 */
export function useVisitorBlocked(opts: {
  publicKey: string | null;
  storage: VisitorStorage;
  live: boolean | null;
  threads: boolean | null;
  sessionContent: boolean | null;
  handshake: boolean;
}): boolean {
  const { publicKey, storage, live, threads, sessionContent, handshake } = opts;
  const [persisted] = useState(() => readPersisted(publicKey, storage));
  // A live event wins. Otherwise any load-time source that says "blocked" wins over one that says
  // "not blocked" (the sources answer at different times; the dangerous mistake is showing a prompt).
  const resolved = useMemo<boolean | null>(() => {
    if (live !== null) return live;
    if (threads === true || sessionContent === true || handshake) return true;
    if (threads === false || sessionContent === false) return false;
    return null;
  }, [live, threads, sessionContent, handshake]);
  useEffect(() => {
    if (resolved === null) return;
    try {
      if (resolved) storage.setItem(blockedKey(publicKey), "1");
      else storage.removeItem(blockedKey(publicKey));
    } catch {
      // Private mode / blocked storage: the in-memory value still holds for this mount.
    }
  }, [resolved, storage, publicKey]);
  return resolved ?? persisted;
}
